import { HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { MaintenanceInterceptor } from '../interceptors';
import { Language, SettingsModel } from '../models';
import { LanguageList } from '../models/language-list';

import { TranslateService } from '@ngx-translate/core';
import { SETTINGS_SERVICE } from '../constants';
import { BaseSettingsGetService } from './base-settings.service';
import { CachedHttpService } from './cached-http.service';
import { HttpService } from './http.service';
import { LocaleLoaderService } from './locale-loader.service';

@Injectable({
  providedIn: 'root'
})
export class LanguageStorageService {
  public initialized = false;

  public get defaultLanguage() {
    return this._defaultLanguage;
  }

  protected languages: Language[] = [];
  protected contentLanguages: Language[] = [];
  protected availableLanguages!: LanguageList;

  protected languageLabelOverrides: { code: string, label: string }[] = [];

  protected readonly englishLang = {
    code: 'en',
    name: 'English',
    enabled: true
  };

  protected _defaultLanguage = 'en';

  constructor(
    protected readonly localeLoaderService: LocaleLoaderService,
    protected readonly translateService: TranslateService,
    @Inject(SETTINGS_SERVICE) protected readonly settingsService: BaseSettingsGetService,
    protected readonly httpService: HttpService,
    protected readonly cacheHttpService: CachedHttpService
  ) { }


  public fetchLanguages(init = true): Observable<void> {
    const params = new HttpParams()
      .set(MaintenanceInterceptor.SKIP_MAINTENANCE_CHECKING_PARAM, init);

    return forkJoin([
      this.settingsService.getSettings(),
      this.cacheHttpService.get<any>('available-languages', params, true)
    ])
      .pipe(
        map(([settings, availableLanguages]: [SettingsModel, LanguageList]) => {
          this.setSupportedLanguages(settings, availableLanguages);
        }),
        mergeMap(() => this.localeLoaderService.loadMultipleLocales()),
        map(() => {
          this.initialized = true;
        })
      );
  }

  public getAvailableLanguages(): Language[] {
    const data = this.availableLanguages?.standard || {};
    const defaultEnglishLang = this.getLanguages().find(item => item.code === this.defaultLanguage);

    const languages = Object.keys(data)
      .map(key => ({ code: key, name: this.getLanguageLabel(key) }));

    if (defaultEnglishLang) {
      languages.push(defaultEnglishLang);
    }

    return languages;
  }

  public getAvailableSpeechLanguages(): Language[] {
    const data = this.availableLanguages?.speech || {};
    const languages = Object.keys(data)
      .map(key => ({ code: key, name: this.getLanguageLabel(key) }));

    return languages;
  }


  public getLanguages(): Language[] {
    return this.languages || [];
  }

  public getContentLanguages(): Language[] {
    return this.contentLanguages || [];
  }

  public getLanguageLabel(code: string, engOnly = false): string {
    const overridenLabel = this.languageLabelOverrides?.find(item => item.code === code)?.label;

    if (overridenLabel) {
      return overridenLabel;
    }

    if (code === this.englishLang.code) {
      return this.englishLang?.name;
    }

    const languageStandartLabels = this.availableLanguages?.standard?.[code];

    if (!languageStandartLabels) {
      return '';
    }

    const { native, english } = languageStandartLabels;

    if ((english === native) || engOnly) {
      return english;
    }

    return `${english} (${native})`;
  }

  protected setSupportedLanguages(settings: SettingsModel, availableLanguages: LanguageList): void {
    const { contentLanguages = {}, interfaceLanguages = {} } = settings?.languages || {};

    this.availableLanguages = availableLanguages;
    this.languageLabelOverrides = settings?.languages?.languageLabelOverrides || [];

    const codes = Object.keys(interfaceLanguages);

    const mappedLanguages = codes.map(key => ({
      code: key,
      enabled: interfaceLanguages[key].enabled,
      name: this.getLanguageLabel(key) || interfaceLanguages[key].label,
      weight: interfaceLanguages[key].weight
    }));

    this.languages = this.sortLanguagesByWeight(mappedLanguages);

    Object.assign(this.englishLang, { name: this.getLanguageLabel(this.englishLang.code) || this.englishLang.name });
    this.languages.unshift(this.englishLang);

    const mappedContentLanguages = settings?.languages?.contentLanguageEnabled ? Object.keys(contentLanguages).map(key => ({
      code: key,
      name: this.getLanguageLabel(key) || contentLanguages[key].label,
      enabled: contentLanguages[key].enabled,
      weight: contentLanguages[key].weight
    })) : [];

    this.contentLanguages = this.sortLanguagesByWeight(mappedContentLanguages);

    if (this.contentLanguages.length) {
      this.contentLanguages.unshift(this.englishLang);
    }

    this.translateService.addLangs(codes);
    this.translateService.setDefaultLang(this.defaultLanguage);
  }

  protected sortLanguagesByWeight(languages: Language[]): Language[] {
    return languages.sort((a, b) => {
      if (a.weight == null || b.weight == null) {
        return 0;
      }
      return a.weight - b.weight;
    });
  }
}
